import { CONTINGENT_WORKERS_OPTIONS } from '../definitions/contingentWorkers';
import { DATA_ADJUSTMENTS_OPTIONS } from '../definitions/dataAdjustments';
import { EXTERNALLY_OWNED_OPTIONS } from '../definitions/externallyOwned';
import { FLIGHT_RISK_SELECTIONS } from '../definitions/flightRisk';
import { FUNDING_STAGE_OPTIONS } from '../definitions/fundingStage';
import {
  defaultMetricMode,
  METRIC_MODE_OPTIONS,
} from '../definitions/metricMode';
import { OVERALL_RATING_OPTIONS } from '../definitions/overallRating';
import {
  defaultPostingSource,
  POSTINGS_SOURCE_OPTIONS,
} from '../definitions/postingsSource';
import { PRESTIGE_SELECTIONS } from '../definitions/prestige';
import { PUBLIC_OPTIONS } from '../definitions/public';
import { REMOTE_SUITABILITY_SELECTIONS } from '../definitions/remoteSuitability';
import type { Option } from '../types/filters';
import { type Filter, FilterId, FilterType } from '../types/filters';

export const STATIC_FILTER_IDS = [
  FilterId.DATA_ADJUSTMENTS,
  FilterId.POSTINGS_SOURCE,
  FilterId.METRIC_MODE,
  FilterId.OVERALL_RATING,
  FilterId.CONTINGENT_WORKERS,
  FilterId.FLIGHT_RISK,
  FilterId.PRESTIGE,
  FilterId.REMOTE_SUITABILITY,
  FilterId.EXTERNALLY_OWNED,
  FilterId.FUNDING_STAGE,
  FilterId.PUBLIC,
] as const;

export const STATIC_FILTER_DATA: Record<
  (typeof STATIC_FILTER_IDS)[number],
  Option[]
> = {
  [FilterId.DATA_ADJUSTMENTS]: DATA_ADJUSTMENTS_OPTIONS,
  [FilterId.POSTINGS_SOURCE]: POSTINGS_SOURCE_OPTIONS,
  [FilterId.METRIC_MODE]: METRIC_MODE_OPTIONS,
  [FilterId.OVERALL_RATING]: OVERALL_RATING_OPTIONS,
  [FilterId.CONTINGENT_WORKERS]: CONTINGENT_WORKERS_OPTIONS,
  [FilterId.FLIGHT_RISK]: FLIGHT_RISK_SELECTIONS,
  [FilterId.PRESTIGE]: PRESTIGE_SELECTIONS,
  [FilterId.REMOTE_SUITABILITY]: REMOTE_SUITABILITY_SELECTIONS,
  [FilterId.EXTERNALLY_OWNED]: EXTERNALLY_OWNED_OPTIONS,
  [FilterId.FUNDING_STAGE]: FUNDING_STAGE_OPTIONS,
  [FilterId.PUBLIC]: PUBLIC_OPTIONS,
} as const;

/** Extra filter option settings for specific use cases
 * ex. Metric Mode is disabled if Posting Source is not Unified (COSMOS)
 */
export const getAdditionalFilterOption = ({
  filter,
  allFilters,
  item,
}: {
  filter: Filter;
  allFilters: Filter[];
  item: Option;
}): Partial<Option> => {
  const filterId = filter.id;

  switch (filterId) {
    case FilterId.METRIC_MODE: {
      const postingSourceNonUnifiedSelected = (() => {
        const filter = allFilters.find(
          (f) => f.id === FilterId.POSTINGS_SOURCE
        );

        if (!filter || filter.type !== FilterType.STATIC_LIST) {
          return;
        }

        return filter.selections.some(
          (selection) => selection.id !== defaultPostingSource.value
        );
      })();

      const isDisabledModeOption =
        postingSourceNonUnifiedSelected &&
        item.value !== defaultMetricMode.value;

      return {
        isDisabled: isDisabledModeOption,
        info: isDisabledModeOption
          ? 'Posting Source must be set to Unified (COSMOS) to enable this option.'
          : undefined,
      };
    }

    default:
      return {};
  }
};
